import React from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useFormContext, useWatch } from 'react-hook-form';
import _get from 'lodash/get';

import { Dropdown as MenuDropdown, DropdownItem, DropdownMenu } from 'components/Dropdown2';
import { CharactersRemaining } from 'components/Form';
import { Input } from 'components/HookForm';
import Message from 'components/Message';
import When from 'components/When';
import { ICONS, PFE_ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';

import SkuEditItemAvailabilityDropdown from 'features/Sku/Items/components/SkuEditItemAvailabilityDropdown';
import SkuItemAvailabilityStatus from 'features/Sku/Items/components/SkuItemAvailabilityStatus';
import { ITEM_AVAILABILITY_STATUS_OPTIONS, SKU_NAME_MAX_INPUT_LENGTH } from 'features/Sku/Items/SkuItems.constants';

import { UNSAVED_OPTION_AVAILABILITY_OPTIONS } from '../Modifiers.availability';
import { isUnsavedOptionId } from '../Modifiers.formState';
import messages from './OptionRowEditor.messages';
import './OptionRowEditor.scss';

const { block, element } = bem('option-row-editor');

const NAME_COUNTER_THRESHOLD = 10;

const OptionRowEditor = ({ fieldName, arrayName, onRemove, disabled, dragHandle }) => {
  const intl = useIntl();
  const { control, getValues } = useFormContext();

  const nameFieldName = `${fieldName}.core.name`;
  const name = useWatch({ control, name: nameFieldName }) || '';
  const availabilityStatus = useWatch({ control, name: `${fieldName}.availabilityStatus` });

  const optionId = useWatch({ control, name: `${fieldName}.core.id` });
  const statusOptions = isUnsavedOptionId(optionId)
    ? UNSAVED_OPTION_AVAILABILITY_OPTIONS
    : ITEM_AVAILABILITY_STATUS_OPTIONS;

  const uniqueName = {
    message: messages.nameNotUnique,
    value: value => {
      const trimmed = (value || '').trim().toLowerCase();
      if (!trimmed) return true;
      const siblings = _get(getValues(), arrayName) || [];
      const duplicates = siblings.filter(option => (_get(option, 'core.name') || '').trim().toLowerCase() === trimmed);
      return duplicates.length <= 1;
    },
  };

  return (
    <div className={block()}>
      <When predicate={Boolean(dragHandle) && !disabled}>
        <button
          type="button"
          ref={dragHandle?.setActivatorNodeRef}
          className={element('handle')}
          aria-label={intl.formatMessage(messages.optionDragHandleAriaLabel, { name })}
          {...(dragHandle?.attributes || {})}
          {...(dragHandle?.listeners || {})}>
          <Isvg className={element('handle-icon')} src={ICONS.dragHandle} />
        </button>
      </When>
      <div className={element('name')}>
        <Input
          className={element('name-input')}
          name={nameFieldName}
          aria-label={intl.formatMessage(messages.optionNameAriaLabel)}
          placeholder={messages.optionNamePlaceholder}
          maxLength={SKU_NAME_MAX_INPUT_LENGTH}
          required={messages.nameRequired}
          validate={uniqueName}
          disabled={disabled}
          noMargin
        />
        <When predicate={SKU_NAME_MAX_INPUT_LENGTH - name.length <= NAME_COUNTER_THRESHOLD}>
          <div className={element('counter', { error: name.length >= SKU_NAME_MAX_INPUT_LENGTH })}>
            <CharactersRemaining length={SKU_NAME_MAX_INPUT_LENGTH} value={name} showMaxLength />
          </div>
        </When>
      </div>

      <div className={element('availability')}>
        {disabled ? (
          <SkuItemAvailabilityStatus status={availabilityStatus} />
        ) : (
          <SkuEditItemAvailabilityDropdown name={`${fieldName}.availabilityStatus`} statusOptions={statusOptions} />
        )}
      </div>

      {!disabled && (
        <MenuDropdown className={element('actions')} attachment="right">
          {({ toggleMenu, closeMenu }) => (
            <>
              <button
                type="button"
                className={element('actions-trigger')}
                aria-label={intl.formatMessage(messages.optionActionsAriaLabel, { name })}
                onClick={toggleMenu}>
                <Isvg className={element('actions-dots')} src={PFE_ICONS.threeDots} />
              </button>
              <DropdownMenu>
                <DropdownItem
                  onClick={() => {
                    closeMenu();
                    onRemove();
                  }}>
                  <Message message={messages.deleteOption} />
                </DropdownItem>
              </DropdownMenu>
            </>
          )}
        </MenuDropdown>
      )}
    </div>
  );
};

OptionRowEditor.propTypes = {
  // Field-array path to this option, e.g. "modifiers[3]".
  fieldName: PropTypes.string.isRequired,
  // Field-array path to the sibling options, e.g. "modifiers" — used for uniqueness.
  arrayName: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  dragHandle: PropTypes.shape({
    setActivatorNodeRef: PropTypes.func,
    attributes: PropTypes.shape({}),
    listeners: PropTypes.shape({}),
  }),
};

export default OptionRowEditor;
